import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import socket from '../../../../utils/socket';
import { showConfirmAlert, showErrorAlert, showSuccessAlert } from '../../../../utils/alerts';
import ReaderStatusIndicator from './ReaderStatusIndicator';

const ReaderReboot = ({ buildingId, doors, loadingDoors }) => {
  const [activeRebootingLocalId, setActiveRebootingLocalId] = useState(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  const rebootableDoors = useMemo(() => {
    return doors.filter((door) => door.localId !== null && door.localId !== undefined && door.localId !== '');
  }, [doors]);

  const emitReaderReboot = ({ buildingId, localId, doorName }) => {
    setActiveRebootingLocalId(localId);

    const handleError = ({ message }) => {
      cleanup();
      setActiveRebootingLocalId(null);
      showErrorAlert('Lo sentimos', message || 'No pudimos reiniciar la lectora en este momento.');
    };

    const handleSuccess = () => {
      cleanup();
      setActiveRebootingLocalId(null);
      showSuccessAlert(
        'Lectora reiniciada',
        `Reinicio enviado correctamente a la lectora ${doorName} (ID: ${localId})`,
      );
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      socket.off('reader:reboot:error', handleError);
      socket.off('reader:reboot:success', handleSuccess);
      cleanupRef.current = null;
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      setActiveRebootingLocalId(null);
      showErrorAlert(
        'Tiempo de espera agotado',
        'La lectora no respondió al comando de reinicio en el tiempo esperado.'
      );
    }, 15000);

    cleanupRef.current = cleanup;

    socket.once('reader:reboot:error', handleError);
    socket.once('reader:reboot:success', handleSuccess);

    socket.emit('frontend:reader:reboot', { buildingId, localId });
  };

  const handleReboot = (door) => {
    const localId = door.localId;
    const doorName = door.name || `Lectora ${localId}`;

    showConfirmAlert({
      title: 'Reiniciar lectora',
      message: `Se enviará un comando de reinicio a "${doorName}" (ID: ${localId}).`,
      cancelButtonText: 'Cancelar',
      confirmText: 'Reiniciar',
      onConfirm: () => {
        emitReaderReboot({
          buildingId,
          localId,
          doorName,
        });
      },
    });
  };

  if (!buildingId) {
    return <p className="text-white">Selecciona un edificio para continuar.</p>;
  }

  if (loadingDoors) {
    return <p className="text-white">Cargando lectoras...</p>;
  }

  if (rebootableDoors.length === 0) {
    return <p className="no-readers-message">No hay lectoras disponibles con ID local en este edificio.</p>;
  }

  const isAnyRebooting = activeRebootingLocalId !== null;

  return (
    <section className="reader-table-container">
      <table className="reader-table">
        <thead>
          <tr>
            <th scope="col">Lectora (ID)</th>
            <th scope="col">Estado Lectoras (SPI)</th>
            <th scope="col">Acción</th>
          </tr>
        </thead>
        <tbody>
          {rebootableDoors.map((door) => {
            const isRebooting = String(door.localId) === String(activeRebootingLocalId);
            const displayName = door.name ? `${door.name} (${door.localId})` : `Lectora (${door.localId})`;

            return (
              <tr key={door.id || door.localId}>
                <th scope="row" className="reader-table-row-header">
                  {displayName}
                </th>
                <td className="reader-telemetry-cell">
                  <ReaderStatusIndicator
                    label="Maestra"
                    status={door.masterStatus}
                    spiOk={door.masterSpiOk}
                  />
                  <ReaderStatusIndicator
                    label="Esclava"
                    status={door.slaveStatus}
                    spiOk={door.slaveSpiOk}
                  />
                </td>
                <td>
                  <button
                    className="btn-table-action"
                    disabled={isAnyRebooting}
                    onClick={() => handleReboot(door)}
                    aria-label={`Reiniciar lectora ${door.name || `Lectora ${door.localId}`} con ID ${door.localId}`}
                  >
                    {isRebooting ? 'Reiniciando...' : 'Reiniciar'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

ReaderReboot.propTypes = {
  buildingId: PropTypes.string,
  doors: PropTypes.array.isRequired,
  loadingDoors: PropTypes.bool,
};

export default ReaderReboot;
